import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  Button,
  IconTile,
  PersonAvatar,
  InputText,
  Tag,
} from '@teo/components';
import {
  Evaluation,
  Training,
  TaskCheckmarkFilledNew,
  OpenExternalLink,
  Person,
  Location,
  City,
  ArrowLeft,
  Document,
  InformationFilled,
  TaskCheckmarkFilled,
} from '@teo/components/icons';
import InfoSection from '@/components/InfoSection';
import UploadDropzoneSection from '@/components/UploadDropzoneSection/UploadDropzoneSection';
import TrainingLearnInfo from '@/components/LearningPath/TrainingLearnInfo';
import { axios } from '@/axios';
import { config } from '@/config';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { useGet } from '@/query';
import { useMobile } from '@/util';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';

const SubmitProgress = ({
  followTraining,
  teacherInfo,
  teachers,
  training,
  trainingId,
  auth,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const submitFollowTraining = async () => {
      if (followTraining?.progress !== 1 && teachers) {
        try {
          for (const teach of teachers) {
            await axios.post(`${config.backend}/notifications`, {
              text: {
                courseName: training?.trainingName,
                username: auth?.user?.username,
              },
              type: 'tocorrect',
              globalLink: `/:lang/follow-up/:organisationId/correction/training/${trainingId}/${auth?.user?.id}`,
              userId: parseInt(teach?.id, 10),
              subject: trainingId,
            });
          }

          await axios.put(
            `${config.backend}/trainingfollow/${followTraining?.trainingId}/${followTraining?.userId}`,
            {
              progress: 1,
              status: 'completed',
            }
          );
        } catch (err) {
          console.error('Error during follow training update:', err);
        }
      }
    };

    submitFollowTraining();
  }, [teachers]);

  return (
    <>
      {followTraining?.feedback && (
        <div className="my-3 flex flex-col rounded-lg bg-[#1e1e200d] py-2 px-3">
          <p className="text-xs font-normal uppercase text-[#919193]">
            {t('widgets.type.feedback')}
          </p>
          <div className="flex items-center py-2">
            <PersonAvatar
              inline
              id={teacherInfo?.id}
              name={teacherInfo?.username}
              imageSrc={teacherInfo?.picture}
            ></PersonAvatar>
            <div className="pl-2 text-sm font-medium">
              {teacherInfo?.username}
            </div>
          </div>
          <p className="text-sm font-normal text-[#4d4d50]">
            {followTraining?.feedback}
          </p>
        </div>
      )}
    </>
  );
};

export const TrainingContent = ({
  training,
  trainingId,
  teachers,
  persons,
  institution,
  followTraining,
  setScore,
  score,
  setMyCertificate,
  myCertificate,
  submitScore,
  preview = false,
  auth,
  originPath,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesctop = useMediaQuery({
    query: '(max-width: 1023px)',
  });
  const isMobile = useMobile();

  let type = null;
  let name = null;
  if (myCertificate) {
    const fullFileName = myCertificate.substring(
      myCertificate.lastIndexOf('/') + 1
    );
    type = fullFileName.substring(fullFileName.lastIndexOf('.') + 1);

    name = fullFileName.substring(0, fullFileName.lastIndexOf('.'));
  }

  const [teacherInfo, setTeacherInfo] = useState(null);

  useGet(`/memberdetails`, {
    enabled: !!followTraining?.teacherId,
    retry: false,
    organisationId: auth?.user?.activeOrganisationId,
    userId: followTraining?.teacherId,
    onSuccess: (result) => {
      setTeacherInfo(result?.data[0] || null);
    },
  });

  const handleGoBack = async () => {
    if (training?.learningpathId) {
      const nodegroup = await axios.get(
        `${config.backend}/nodegroups?type=training&objectId=${training?.id}&userId=${auth?.user?.id}`
      );

      if (nodegroup?.data[0]?.depth > 1) {
        navigate(`${originPath}/themes/${training?.learningpathId}`);
      } else {
        navigate(`${originPath}`);
      }
    }
  };

  return (
    <div className="flex w-full flex-col lg:flex-row">
      <div className={`mx-auto w-full max-w-3xl flex-col`}>
        {auth && !preview && (
          <>
            {isMobile ? (
              <MobileSubPageMenu backFn={() => navigate(`${originPath}`)} />
            ) : (
              <Button
                className="flex items-center gap-3"
                variant="text"
                // onClick={() => navigate(`${originPath}`)}
                onClick={() => handleGoBack()}
              >
                <ArrowLeft className="h-5 w-5" />
                <p>{t('pages.lp_landing.back_to_lpgroup')}</p>
              </Button>
            )}
          </>
        )}
        <div className="flex pt-4">
          <IconTile
            Icon={
              training?.trainingType === 'evaluation' ? Evaluation : Training
            }
            color={training?.trainingType === 'evaluation' ? 'purple' : 'green'}
            size="small_lg"
          />
        </div>
        <div className="flex flex-col pt-4">
          <p className="text-sm text-grey-05">
            {training?.typeOfTraining === 'external'
              ? training?.trainingType === 'evaluation'
                ? t('pages.landing_training.external_e')
                : t('pages.landing_training.external_t')
              : training?.typeOfTraining === 'internal'
              ? training?.trainingType === 'evaluation'
                ? t('pages.landing_training.internal_e')
                : t('pages.landing_training.internal_t')
              : t('pages.landing_training.online_t')}
          </p>
          <p className={`pt-1 pb-4 text-xl font-semibold text-black`}>
            {training?.trainingName}
          </p>
          <p className={`text-base text-grey-08  `}>
            {training?.trainingDescription}
          </p>
          {training?.typeOfTraining === 'internal' && teachers?.length > 0 && (
            <div className="flex flex-col pt-4">
              <p className="pb-1 text-sm text-grey-05">
                {t('pages.landing_training.evaluator')}
              </p>
              <div className="flex w-max flex-col gap-2">
                {teachers?.map((tech) => {
                  return (
                    <div
                      key={tech?.userId}
                      className="flex items-center rounded-lg border p-3"
                    >
                      <PersonAvatar
                        inline
                        id={tech?.userId}
                        name={tech?.username || ''}
                        imageSrc={tech?.picture || undefined}
                      ></PersonAvatar>
                      <div className="text-sm font-medium text-grey-08">
                        {tech.username}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {training?.typeOfTraining !== 'internal' &&
            training?.uploadCertificate && (
              <div className="flex items-center pt-4">
                <TaskCheckmarkFilledNew className="h-4 w-4 text-grey-04" />
                <p className="pl-1 text-sm text-grey-05">
                  {t('pages.landing_training.certificate')}
                </p>
              </div>
            )}
        </div>
        {!followTraining?.isCorrected &&
          training?.trainingType === 'training' && (
            <div className="py-8">
              <InfoSection
                text={t('pages.landing_training.info_text_internal')}
              />
            </div>
          )}

        {!followTraining?.isCorrected &&
          training?.trainingType === 'evaluation' && (
            <div className="py-8">
              <InfoSection
                text={t('pages.landing_training.info_text_internal_ev')}
              />
            </div>
          )}

        {followTraining?.progress === 1 && !followTraining?.isCorrected && (
          <div className="my-2">
            <div className="flex w-full flex-col gap-1 rounded-lg bg-tint-blue-light p-5">
              <InformationFilled className="h-6 w-6 text-tint-blue" />
              <p
                className="text-sm text-[#004795]"
                dangerouslySetInnerHTML={{
                  __html: t('pages.lp_landing.notCorectCard', {
                    subTypeDem: t(`types.${training?.trainingType}Dem`),
                  }),
                }}
              ></p>
            </div>
          </div>
        )}

        {followTraining?.progress === 1 && followTraining?.isCorrected && (
          <>
            {followTraining?.score >= training?.minScore ? (
              <div className="my-4 flex items-center gap-2">
                <TaskCheckmarkFilled className="h-6 w-6 text-success-05" />
                <p className="text-sm text-success-05">
                  {t('pages.landing_training.successfully_complete')}
                </p>
              </div>
            ) : (
              <div className="my-2">
                <div className="flex w-full flex-col gap-1 rounded-lg bg-tint-blue-light p-5">
                  <InformationFilled className="h-6 w-6 text-tint-blue" />
                  <p
                    className="text-sm text-[#004795]"
                    dangerouslySetInnerHTML={{
                      __html: t('pages.lp_landing.notPassCard', {
                        subTypeDef: t(`types.${training?.trainingType}Def`),
                      }),
                    }}
                  ></p>
                </div>
              </div>
            )}
          </>
        )}

        {training?.url &&
          isDesctop &&
          training?.typeOfTraining !== 'internal' && (
            <div className="mt-6 -ml-4 -mr-4 flex flex-col border-t px-4 pt-6 md:ml-0 md:mr-0 md:px-0">
              <p className="pb-2 text-base font-medium text-grey-06">
                {t('pages.landing_training.subscription')}
              </p>
              <p className="text-sm text-grey-08">
                {t('pages.landing_training.subscription_text')}
              </p>
              <div className="mt-6">
                <Button
                  variant="outline"
                  className="flex items-center"
                  onClick={() => window.open(training?.url)}
                >
                  {t('pages.landing_training.subscription_btn')}
                  <OpenExternalLink className="ml-1 h-4 w-4 text-grey-04" />
                </Button>
              </div>
            </div>
          )}
        {(training?.typeOfTraining === 'external' ||
          training?.trainingType !== 'evaluation') &&
        (training?.inputScore || training?.uploadCertificate) &&
        (training?.inputScoreType === 'studentOrEvaluator' ||
          training?.inputScoreType === 'student') ? (
          <div className="mt-6 -ml-4 -mr-4 flex flex-col border-t py-6 px-4 md:mt-8 md:ml-0 md:mr-0 md:py-8 md:px-0">
            <p className="pb-2 text-base font-medium text-grey-06">
              {t('pages.landing_training.results')}
            </p>
            {training?.inputScore &&
              (training?.inputScoreType === 'studentOrEvaluator' ||
                training?.inputScoreType === 'student') && (
                <div className="mb-6">
                  <p className="text-sm text-grey-08">
                    {training?.trainingType === 'training' &&
                      t('pages.landing_training.add_score_certificate')}

                    {training?.trainingType === 'evaluation' &&
                      t('pages.landing_training.add_score_certificate_ev')}
                  </p>
                  <div className="mt-6 w-max">
                    <p className="mb-1 text-sm font-semibold text-black">
                      {t('pages.landing_training.your_score')}
                    </p>
                    <InputText
                      disabled={followTraining?.progress === 1 || preview}
                      type="number"
                      style={{ width: '140px' }}
                      value={score}
                      onChange={(ev) => {
                        const sc = ev.target.value;
                        +sc < 101 && setScore(ev.target.value);
                      }}
                      suffix="%"
                    />
                  </div>
                  {followTraining?.progress === 1 &&
                    followTraining?.isCorrected &&
                    followTraining?.score < training?.minScore && (
                      <div className="mt-2 text-sm text-error-04">
                        {t('pages.course_content_flow.step_3.minimum_score')} -{' '}
                        {Math.round(training?.minScore * 100)}%
                      </div>
                    )}
                </div>
              )}
            {followTraining?.feedback && (
              <div className="my-3 flex flex-col rounded-lg bg-[#1e1e200d] py-2 px-3">
                <p className="text-xs font-normal uppercase text-[#919193]">
                  {t('widgets.type.feedback')}
                </p>
                <div className="flex items-center py-2">
                  <PersonAvatar
                    inline
                    id={teacherInfo?.id}
                    name={teacherInfo?.username}
                    imageSrc={teacherInfo?.picture}
                  ></PersonAvatar>
                  <div className="pl-2 text-sm font-medium">
                    {teacherInfo?.username}
                  </div>
                </div>
                <p className="text-sm font-normal text-[#4d4d50]">
                  {followTraining?.feedback}
                </p>
              </div>
            )}
            {training?.uploadCertificate &&
              (training?.inputScoreType === 'studentOrEvaluator' ||
                training?.inputScoreType === 'student') && (
                <div className="mb-6">
                  <p className="mb-1 text-sm font-semibold text-black">
                    {t('pages.landing_training.upload_certificate')}
                  </p>
                  {followTraining?.progress === 1 &&
                  followTraining?.certificates?.length > 0 ? (
                    <div className="flex items-center justify-between rounded-lg border border-[#eaeaed] p-3">
                      <div className="flex items-center">
                        {myCertificate.includes('.pdf') ? (
                          <a
                            href={myCertificate}
                            target="_blank"
                            rel="noreferrer"
                            className="mr-3 flex h-16 w-16 rounded border border-error-02 bg-error-01 text-error-05"
                          >
                            <Document className="m-auto w-10" />
                          </a>
                        ) : (
                          <img
                            alt="logo"
                            src={myCertificate}
                            className={`mr-3 h-16 flex-[1_0_64px] rounded object-cover object-center`}
                          />
                        )}
                        <div>
                          <p
                            className={`
                                          overflow-hidden
                                          truncate
                                          text-base
                                          font-semibold
                                        `}
                          >
                            {name}
                          </p>

                          {myCertificate.includes('.pdf') ? (
                            <Tag
                              documentType="PDF"
                              title="PDF"
                              variant="document"
                            />
                          ) : (
                            <Tag
                              title={`${type}`}
                              size="md"
                              variant="document"
                              documentType="image"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <UploadDropzoneSection
                      setUploadLogo={setMyCertificate}
                      uploadLogo={myCertificate}
                      onlyFile={true}
                      replaceBtn={true}
                    />
                  )}
                </div>
              )}
            <div className="mb-6 -mt-3">
              <UploadDropzoneSection
                setUploadLogo={setMyCertificate}
                uploadLogo={myCertificate}
                onlyFile={true}
                replaceBtn={true}
              />
            </div>
            <div>
              {preview ? (
                <Button disabled={preview}>
                  {t('pages.landing_training.submit_results')}
                </Button>
              ) : (
                <Button
                  disabled={
                    training?.uploadCertificate && training?.inputScore
                      ? training?.inputScoreType === 'studentOrEvaluator' ||
                        training?.inputScoreType === 'student'
                        ? myCertificate &&
                          score > 0 &&
                          followTraining?.progress !== 1
                          ? false
                          : true
                        : myCertificate && followTraining?.progress !== 1
                        ? false
                        : true
                      : training?.uploadCertificate
                      ? myCertificate && followTraining?.progress !== 1
                        ? false
                        : true
                      : training?.inputScore
                      ? training?.inputScoreType === 'studentOrEvaluator' ||
                        training?.inputScoreType === 'student'
                        ? score > 0 && followTraining?.progress !== 1
                          ? false
                          : true
                        : false
                      : false
                  }
                  onClick={() => submitScore()}
                >
                  {t('pages.landing_training.submit_results')}
                </Button>
              )}
            </div>
          </div>
        ) : (
          !preview && (
            <SubmitProgress
              followTraining={followTraining}
              teacherInfo={teacherInfo}
              teachers={teachers}
              training={training}
              trainingId={trainingId}
              auth={auth}
            />
          )
        )}
      </div>
      {!isDesctop ? (
        <>
          {(training?.url ||
            persons?.length > 0 ||
            training?.location ||
            institution) && (
            <TrainingLearnInfo
              training={training}
              persons={persons}
              institution={institution}
            />
          )}
        </>
      ) : (
        <div className="-ml-4 -mr-4 md:ml-0 md:mr-0">
          {persons?.length > 0 && training?.typeOfTraining !== 'online' && (
            <div className="flex flex-col border-t px-4 py-6 md:px-0 md:py-8">
              <p className="pb-2 text-base font-medium text-grey-06">
                {t('pages.landing_training.contact')}
              </p>
              {persons?.map((person) => {
                return (
                  <div
                    key={person?.id + '_' + person?.trainingId}
                    className="mb-2 flex rounded-lg border border-grey-02 p-4"
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex w-full">
                        <Person className="h-5 w-5" />
                      </div>

                      <h4>{person?.contactName}</h4>
                      <a
                        className="text-sm text-[#1173DD]"
                        href="mailto:info@teo.training"
                      >
                        {person?.contactEmail}
                      </a>
                      <p className="text-sm text-[#1E1E20A8]">
                        {person?.contactPhone}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {training?.location && training?.typeOfTraining !== 'online' && (
            <div className="flex flex-col border-t px-4 py-6 md:px-0 md:py-8">
              <p className="pb-2 text-base font-medium text-grey-06">
                {t('pages.landing_training.location')}
              </p>
              <div className="mb-2 flex rounded-lg border border-grey-02 p-4">
                <div className="flex flex-col gap-2">
                  <div className="flex w-full">
                    <Location className="h-5 w-5" />
                  </div>
                  <h4 className="whitespace-pre-line">{training?.location}</h4>
                  <a
                    className="text-sm text-[#1173DD]"
                    href={`https://www.google.com/maps?q=${encodeURIComponent(
                      training?.location?.replace(/\n/g, ' ')
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('pages.landing_training.get_directions')}
                  </a>
                </div>
              </div>
            </div>
          )}
          {institution && training?.typeOfTraining === 'external' && (
            <div className="flex flex-col border-t px-4 py-6 md:px-0 md:py-8">
              <p className="pb-2 text-base font-medium text-grey-06">
                {t('pages.landing_training.institution')}
              </p>

              <div className="mb-2 flex rounded-lg border border-grey-02 p-4">
                <div className="flex flex-col gap-2">
                  <div className="flex w-full">
                    <City className="h-5 w-5" />
                  </div>

                  <h4>{institution?.contactName}</h4>
                  <a
                    className="text-sm text-[#1173DD]"
                    href="mailto:info@teo.training"
                  >
                    {institution?.contactEmail}
                  </a>
                  <p className="text-sm text-[#1E1E20A8]">
                    {institution?.contactPhone}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
